import { ContentCopy, Download, ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import messages from '../../intl/messages';
import { NOTIF_ACTION_TYPES } from '../../store/notif/actions';

const RAW_JSON_PREVIEW_LIMIT = 2000;

const LogRawData = ({ examId, entries, summary }) => {
	const intl = useIntl();
	const dispatch = useDispatch();
	const [expanded, setExpanded] = useState(false);

	// full payload (entries + summary), regardless of the <pre> preview limit below - used by copy/download
	const fullPayload = useMemo(() => ({ entries, summary }), [entries, summary]);

	const previewJson = useMemo(() => {
		if (!expanded) return '';
		if (entries.length <= RAW_JSON_PREVIEW_LIMIT) return JSON.stringify(fullPayload, null, 2);
		return JSON.stringify({ ...fullPayload, entries: entries.slice(0, RAW_JSON_PREVIEW_LIMIT) }, null, 2);
	}, [expanded, entries, fullPayload]);

	const notify = (type, message) => dispatch({ type, payload: { type, message } });

	const handleCopy = (e) => {
		e.stopPropagation();
		navigator.clipboard
			.writeText(JSON.stringify(fullPayload, null, 2))
			.then(() => notify(NOTIF_ACTION_TYPES.SUCCESS, intl.formatMessage(messages.copiedIntoClipboard)))
			.catch((error) => notify(NOTIF_ACTION_TYPES.ERROR, String(error)));
	};

	const handleDownload = (e) => {
		e.stopPropagation();
		const blob = new Blob([JSON.stringify(fullPayload, null, 2)], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `mobile-camera-log-${examId}.json`;
		link.click();
		URL.revokeObjectURL(url);
	};

	return (
		<Accordion expanded={expanded} onChange={(e, isExpanded) => setExpanded(isExpanded)} variant="outlined" elevation={0}>
			<AccordionSummary expandIcon={<ExpandMore />}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
					<Typography>
						{intl.formatMessage(messages.mobileLogRawData)} ({entries.length})
					</Typography>
					<Box>
						<Tooltip title={intl.formatMessage(messages.mobileLogCopyJson)}>
							<IconButton size="small" onClick={handleCopy}>
								<ContentCopy fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title={intl.formatMessage(messages.mobileLogDownloadJson)}>
							<IconButton size="small" onClick={handleDownload}>
								<Download fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
			</AccordionSummary>
			<AccordionDetails>
				<Box component="pre" sx={{ maxHeight: 320, overflow: 'auto', fontFamily: 'monospace', fontSize: 12, m: 0 }}>
					{previewJson}
				</Box>
				{entries.length > RAW_JSON_PREVIEW_LIMIT && (
					<Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
						{intl.formatMessage(messages.mobileLogRawTruncated)}
					</Typography>
				)}
			</AccordionDetails>
		</Accordion>
	);
};

export default LogRawData;
